import React from 'react';
import { ClassCode, ObstacleType, isSpecialObstacleAllowed } from '../types/drf-rules';
import { ObstaclePool } from '../utils/course-generator';

interface ObstaclePaletteProps {
  pool: ObstaclePool;
  classCode: ClassCode;
  selectedType: string | null;
  onTypeSelect: (type: string | null) => void;
}

const ObstaclePalette: React.FC<ObstaclePaletteProps> = ({
  pool,
  classCode,
  selectedType,
  onTypeSelect
}) => {
  const obstacleTypes: { type: ObstacleType; name: string; description: string }[] = [
    { type: 'vertical', name: 'Enkelt Spring', description: 'Standard vertikalt spring' },
    { type: 'oxer', name: 'Oxer', description: 'Breddespring med to stænger' },
    { type: 'triple_bar', name: 'Triple Bar', description: 'Tredobbelt breddespring' },
    { type: 'liverpool', name: 'Liverpool', description: 'Spring med vandbakke' },
    { type: 'open_water', name: 'Vandgrav', description: 'Åben vandgrav' },
    { type: 'start', name: 'Start', description: 'Start markering for banen' },
    { type: 'finish', name: 'Slut', description: 'Slut markering for banen' }
  ];

  const getObstacleIcon = (type: ObstacleType) => {
    const baseClasses = "w-8 h-8 rounded border-2 flex items-center justify-center text-white font-bold text-xs";
    
    switch (type) {
      case 'vertical':
        return <div className={`${baseClasses} bg-purple-500 border-purple-600`}>V</div>;
      case 'oxer':
        return <div className={`${baseClasses} bg-amber-500 border-amber-600`}>O</div>;
      case 'triple_bar':
        return <div className={`${baseClasses} bg-emerald-500 border-emerald-600`}>T</div>;
      case 'liverpool':
        return <div className={`${baseClasses} bg-blue-500 border-blue-600`}>L</div>;
      case 'open_water':
        return <div className={`${baseClasses} bg-cyan-500 border-cyan-600`}>W</div>;
      case 'start':
        return <div className={`${baseClasses} bg-green-500 border-green-600`}>S</div>;
      case 'finish':
        return <div className={`${baseClasses} bg-red-500 border-red-600`}>F</div>;
      default:
        return <div className={`${baseClasses} bg-gray-500 border-gray-600`}>?</div>;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Spring Palette</h3>
      
      <div className="space-y-3">
        {obstacleTypes.map(({ type, name, description }) => {
          const count = pool[type as keyof ObstaclePool];
          const isAllowed = isSpecialObstacleAllowed(classCode, type);
          const isAvailable = count > 0 && isAllowed;
          const isSelected = selectedType === type;
          
          return (
            <button
              key={type}
              onClick={() => onTypeSelect(isSelected ? null : type)}
              disabled={!isAvailable}
              className={`
                w-full p-3 rounded-lg border-2 transition-all duration-200 text-left
                ${isSelected 
                  ? 'border-blue-500 bg-blue-50 shadow-md' 
                  : isAvailable 
                    ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50' 
                    : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-50'
                }
              `}
            >
              <div className="flex items-center space-x-3">
                {getObstacleIcon(type)}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${isAvailable ? 'text-gray-900' : 'text-gray-500'}`}>
                      {name}
                    </span>
                    <span className={`text-sm px-2 py-1 rounded-full ${
                      isAvailable 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {isAllowed ? count : 'Ikke tilladt'}
                    </span>
                  </div>
                  <p className={`text-sm mt-1 ${isAvailable ? 'text-gray-600' : 'text-gray-400'}`}>
                    {description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Klasse: {classCode}</h4>
        <div className="text-sm text-blue-800 space-y-1">
          <p>• Anbefalede spring for denne klasse</p>
          <p>• Kombinationer tilladt: {pool.combination}</p>
          <p>• Klik for at vælge et værktøj</p>
          <p>• Klik igen for at fravælge</p>
        </div>
      </div>
    </div>
  );
};

export default ObstaclePalette; 